import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Checkbox, CustomThemeProvider, Popover } from 'sonata-react-components';
import { useTheme } from 'styled-components';

import type { ButtonUIProps } from 'sonata-react-components';

import './columnVisibility.scss';

export type ColumnVisibilityItem = {
  /** Mandatory columns (e.g. the entity name) are listed checked and cannot be toggled. */
  disabled?: boolean;
  id: string;
  label: string;
  visible: boolean;
};

type Props = {
  columns: ColumnVisibilityItem[];
  prefixId: string;
  /** Columns "Reset to default" leaves visible. Every column when omitted. */
  defaultVisibleColumnIds?: string[];
  /** Text of the trigger button. Icon-only when omitted. */
  label?: string;
  /** Receives the ids of the columns to show once the user applies the selection. */
  onApply: (visibleColumnIds: string[]) => void;
  /** Size of the trigger button; the button's own default when omitted. */
  size?: ButtonUIProps['size'];
};

type Draft = Record<string, boolean>;

const toDraft = (columns: ColumnVisibilityItem[], allVisible = false): Draft =>
  Object.fromEntries(columns.map(column => [column.id, allVisible || column.visible]));

const toDefaultDraft = (columns: ColumnVisibilityItem[], defaultVisibleColumnIds?: string[]): Draft =>
  defaultVisibleColumnIds
    ? Object.fromEntries(columns.map(column => [column.id, defaultVisibleColumnIds.includes(column.id)]))
    : toDraft(columns, true);

const areDraftsEqual = (a: Draft, b: Draft): boolean => {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every(key => a[key] === b[key]);
};

/**
 * Menu to choose which list columns are displayed. Changes are kept as a draft and only
 * reported through `onApply`; the visibility state itself is owned by the tab.
 */
export function ColumnVisibility({
  columns,
  prefixId,
  onApply,
  defaultVisibleColumnIds,
  label,
  size
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  // The menu is portaled out of the themed tree, so the theme wrapper is re-applied for SRC styles (e.g. checkboxes).
  const { themeContrast } = useTheme() as { themeContrast: 'light' | 'dark' | 'darkAI' };
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>({});
  const hiddenCount = columns.filter(column => !column.visible).length;
  const title = formatMessage({ defaultMessage: 'Select columns to display', id: 'campaigns.columnVisibility.title' });
  const resetLabel = formatMessage({ defaultMessage: 'Reset to default', id: 'campaigns.columnVisibility.reset' });

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setDraft(toDraft(columns));
    }
    setIsOpen(open);
  };

  const handleReset = () => setDraft(toDefaultDraft(columns, defaultVisibleColumnIds));

  const handleApply = () => {
    onApply(columns.filter(column => column.disabled || draft[column.id]).map(column => column.id));
    setIsOpen(false);
  };

  const isResetDisabled = areDraftsEqual(draft, toDefaultDraft(columns, defaultVisibleColumnIds));
  const isApplyDisabled = areDraftsEqual(draft, toDraft(columns));

  return (
    <Popover onOpenChange={handleOpenChange} open={isOpen} placement="bottom-end">
      <Popover.Trigger asChild>
        <ButtonUI
          icon="fa-regular fa-table-columns"
          id={`${prefixId}_columnVisibilityButton`}
          onClick={() => handleOpenChange(!isOpen)}
          shape={label ? undefined : 'squareRounded'}
          size={size}
          text={label}
          title={title}
          type="secondary"
          {...(hiddenCount > 0 && {
            badge: { className: 'columnVisibility__badge', content: hiddenCount }
          })}
        />
      </Popover.Trigger>
      <Popover.Content className="columnVisibilityPopover" id={`${prefixId}_columnVisibilityMenu`}>
        <CustomThemeProvider themeContrast={themeContrast}>
          <div className="columnVisibility">
            <span className="columnVisibility__title">{title}</span>
            <ul aria-label={title} className="columnVisibility__list">
              {columns.map(column => (
                <li key={column.id}>
                  <Checkbox
                    active={Boolean(column.disabled || draft[column.id])}
                    disabled={column.disabled}
                    id={`${prefixId}_columnVisibility_${column.id}`}
                    labelText={column.label}
                    onClickAction={(_: string, checked: boolean) =>
                      setDraft(previous => ({ ...previous, [column.id]: checked }))
                    }
                    tiny
                  />
                </li>
              ))}
            </ul>
            <div className="columnVisibility__footer">
              <ButtonUI
                className="columnVisibility__reset"
                disabled={isResetDisabled}
                icon="fa-regular fa-rotate-right"
                id={`${prefixId}_columnVisibilityReset`}
                onClick={handleReset}
                shape="link"
                size="small"
                text={resetLabel}
                type="secondary"
              />
              <ButtonUI
                className="columnVisibility__apply"
                disabled={isApplyDisabled}
                id={`${prefixId}_columnVisibilityApply`}
                onClick={handleApply}
                text={formatMessage({ defaultMessage: 'Apply filters', id: 'campaigns.columnVisibility.apply' })}
                type="secondary"
              />
            </div>
          </div>
        </CustomThemeProvider>
      </Popover.Content>
    </Popover>
  );
}
